/**
 * Light, dark, or whatever the machine says.
 *
 * tokens.css follows prefers-color-scheme and can be forced either way with data-theme on the
 * root element. There was no way to ask for dark on a light machine, so the dark theme was
 * awkward to look at deliberately, which is a large part of why so much of it shipped broken:
 * a card with no colour, state marks at 1.4:1, an invisible draft hatch (owner, 2026-09-21).
 *
 * "system" stores nothing and removes the attribute, so the media query decides again.
 */
export type Theme = 'light' | 'dark' | 'system';
export const THEME_KEY = 'timefly.theme';

export function readTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

/** Sets the attribute and remembers the choice. Safe to call before React mounts. */
export function applyTheme(t: Theme): void {
  const root = document.documentElement;
  if (t === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', t);
  try {
    if (t === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, t);
  } catch {
    /* a browser with storage blocked still gets the theme, just not the memory of it */
  }
}

/** What "system" currently resolves to, for a control that wants to show the effective theme. */
export const systemIsDark = (): boolean =>
  typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
